<template>
  <CustomModal
    v-model:visible="modalVisible"
    title="Add Notetaker to your meeting"
    width="480px"
    @close="handleCancel"
  >
    <template #default>
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <label class="text_color font-normal text-sm">Platform</label>
          <a-select
            v-model:value="notetakerForm.platform"
            placeholder="Select platform"
            class="w-full"
          >
            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_arrow"
                class="size-4"
              />
            </template>
            <a-select-option
              v-for="option in platformOptions"
              :key="option.value"
              :value="option.value"
            >
              <div class="flex items-center gap-2">
                <ThemeImage
                  :icon="option.icon"
                  :alt="option.label"
                  class="size-4"
                />
                <span>{{ option.label }}</span>
              </div>
            </a-select-option>
          </a-select>
        </div>

        <div class="flex flex-col gap-2">
          <label class="text_color font-normal text-sm">Meeting URL</label>
          <a-input
            v-model:value="notetakerForm.meeting_url"
            class="input_medium"
            placeholder="Enter meeting URL"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex gap-2 justify-end">
        <a-button class="secondary_medium" @click="handleCancel">
          Cancel
        </a-button>
        <a-button
          class="primary_medium"
          @click="handleAdd"
          :loading="loading"
          :disabled="!isFormValid"
        >
          Add
        </a-button>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { ref, computed } from "vue";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import DashboardService from "@/src/api/DashboardService.js";
import CustomModal from "@/src/components/CustomModal.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:visible", "success"]);

const modalVisible = computed({
  get: () => props.visible,
  set: (value) => emit("update:visible", value),
});

const loading = ref(false);
const notetakerForm = ref({
  platform: null,
  meeting_url: "",
});

const platformOptions = [
  { value: "google_meet", label: "Google Meet", icon: imageNames.GOOGLE_MEET },
  { value: "zoom", label: "Zoom", icon: imageNames.ZOOM_ICON },
  { value: "ms_teams", label: "MS Teams", icon: imageNames.MS_TEAMS },
];

// URL patterns for each platform
const platformUrlPatterns = {
  google_meet: /meet\.google\.com/i,
  zoom: /zoom\.(us|com)/i,
  ms_teams: /teams\.(microsoft\.com|live\.com)/i,
};

// Computed property to check if form is valid for enabling the Add button
const isFormValid = computed(() => {
  return notetakerForm.value.platform && notetakerForm.value.meeting_url?.trim();
});

// Validate if URL matches the selected platform
const validateUrlMatchesPlatform = () => {
  const { platform, meeting_url } = notetakerForm.value;
  if (!platform || !meeting_url) return true;

  const pattern = platformUrlPatterns[platform];
  if (!pattern) return true;

  return pattern.test(meeting_url);
};

const getPlatformLabel = (platformValue) => {
  const option = platformOptions.find((opt) => opt.value === platformValue);
  return option?.label || platformValue;
};

const resetForm = () => {
  notetakerForm.value = {
    platform: null,
    meeting_url: "",
  };
};

const handleCancel = () => {
  modalVisible.value = false;
  resetForm();
};

const handleAdd = async () => {
  try {
    if (!notetakerForm.value.platform) {
      message.error("Please select a platform");
      return;
    }
    if (!notetakerForm.value.meeting_url) {
      message.error("Please enter a meeting URL");
      return;
    }

    // Validate URL matches selected platform
    if (!validateUrlMatchesPlatform()) {
      const platformLabel = getPlatformLabel(notetakerForm.value.platform);
      message.error(`The meeting URL does not match the selected platform (${platformLabel})`);
      return;
    }

    loading.value = true;
    const payload = {
      platform: notetakerForm.value.platform,
      meeting_url: notetakerForm.value.meeting_url,
    };

    const response = await DashboardService.createMeetingBot(payload);
    if (response.success) {
      message.success("Notetaker added successfully");
      modalVisible.value = false;
      resetForm();
      emit("success");
    } else {
      message.error(response.message || "Failed to add notetaker");
    }
  } catch (error) {
    console.error("Error adding notetaker:", error);
    message.error("An error occurred while adding notetaker");
  } finally {
    loading.value = false;
  }
};
</script>
